import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  Stack,
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  moduleAcknowledgmentService,
  RecordAcknowledgmentResponse,
} from '../services/moduleAcknowledgmentService';

// FEAT-046 — Per-module Acknowledgment Gate (MVP-18.20).
//
// Rendered at the end of a learning module video (driven by FEAT-023). Until
// the caregiver checks the warm attestation and confirms, the surrounding
// page must not flip the module to "complete". Idempotent on retry: a
// duplicate POST returns 200 + alreadyExisted, so this surface is safe to
// re-render across navigation.
//
// Copy follows the §1.2 elderly-friendly tone — warm, confidence-building,
// no compliance-speak. No "Required: confirm attestation" — instead, "I've
// watched this module and understand the material".

export interface ModuleAcknowledgmentGateProps {
  moduleId: string;
  contentVersion?: number;
  /** Already-recorded ack (e.g. caregiver returned later) — gate auto-resolves. */
  initiallyAcknowledged?: boolean;
  /** Called once the ack is persisted (first time OR replay). */
  onAcknowledged?: (response: RecordAcknowledgmentResponse) => void;
}

const ATTESTATION_LABEL =
  "I've watched this module and understand the material";

export default function ModuleAcknowledgmentGate({
  moduleId,
  contentVersion = 1,
  initiallyAcknowledged = false,
  onAcknowledged,
}: ModuleAcknowledgmentGateProps) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(initiallyAcknowledged);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!checked || submitting || acknowledged) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await moduleAcknowledgmentService.recordAcknowledgment({
        moduleId,
        contentVersion,
        method: 'CHECKBOX',
      });
      setAcknowledged(true);
      onAcknowledged?.(result);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We could not save your acknowledgment right now. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (acknowledged) {
    return (
      <Card
        data-testid="module-ack-gate-complete"
        sx={{ borderLeft: '4px solid', borderColor: 'success.main' }}
      >
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CheckCircleIcon color="success" aria-hidden="true" />
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Thank you for confirming — you can move on whenever you're ready.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your acknowledgment has been saved to your learning record.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      data-testid="module-ack-gate"
      sx={{ borderLeft: '4px solid', borderColor: 'primary.main' }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
          <VerifiedUserIcon color="primary" aria-hidden="true" />
          <Box>
            <Typography variant="h6" component="h2" gutterBottom>
              One small step to wrap up this module
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please confirm you watched the lesson and feel comfortable with
              what was shared. Your confirmation goes in your learning record.
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="module-ack-gate-error">
            {error}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              inputProps={
                {
                  'aria-label': ATTESTATION_LABEL,
                  'data-testid': 'module-ack-gate-checkbox',
                } as unknown as React.InputHTMLAttributes<HTMLInputElement>
              }
            />
          }
          label={ATTESTATION_LABEL}
        />

        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            disabled={!checked || submitting}
            onClick={handleConfirm}
            data-testid="module-ack-gate-confirm"
          >
            {submitting ? 'Saving…' : 'Confirm and finish'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
